import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    // A replaced/reconnected gallery needs a fresh listener controller.
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    // Warm only the assigned colour images, not the entire product gallery.
    this.#warmColourImages();
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener('change', this.#handleImmediateColourChange, { signal, capture: true });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Select an existing slide before the normal variant request completes.
   * Desktop grid layouts keep their existing server-rendered behaviour.
   * @param {Event} event
   */
  #handleImmediateColourChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;

    const picker = input.closest('variant-picker.huntech-product-page-picker');
    if (!(picker instanceof HTMLElement)) return;
    if (picker.closest('product-card, quick-add-dialog, dialog')) return;

    // Do not apply a current product's images to a combined-listing navigation.
    const connectedUrl = input.dataset.connectedProductUrl;
    if (connectedUrl) {
      const currentUrl = picker.dataset.productUrl;
      if (!currentUrl) return;
      if (new URL(connectedUrl, location.origin).pathname !== new URL(currentUrl, location.origin).pathname) return;
    }

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (this.presentation !== 'carousel' && !isMobile) return;

    const mapElement = this.querySelector('script[data-huntech-media-map]');
    if (!mapElement?.textContent) return;

    try {
      const map = JSON.parse(mapElement.textContent);
      if (map.productId !== picker.dataset.productId) return;
      const mediaId = map.options?.[input.dataset.optionValueId ?? ''];
      const index = map.indices?.[mediaId];
      if (!Number.isInteger(index) || index < 0) return;
      if (!this.slideshow) return;

      this.slideshow.select(index, undefined, { animate: false });
    } catch (error) {
      // The normal variant refresh remains available if the local lookup fails.
      console.warn('Immediate gallery selection unavailable:', error);
    }
  };

  #warmColourImages() {
    const mapElement = this.querySelector('script[data-huntech-media-map]');
    if (!mapElement?.textContent) return;
    try {
      const map = JSON.parse(mapElement.textContent);
      const colourIds = new Set(Object.values(map.options ?? {}).filter(Boolean));
      this.querySelectorAll('slideshow-component .product-media[data-media-id]').forEach((media) => {
        if (!colourIds.has(media.getAttribute('data-media-id'))) return;
        const image = media.querySelector('img');
        if (!(image instanceof HTMLImageElement)) return;
        image.loading = 'eager';
        // Allow the browser to fetch and decode before the customer selects it.
        void image.decode().catch(() => {});
      });
    } catch (error) {
      console.warn('Colour image warming unavailable:', error);
    }
  }

  /** @param {VariantUpdateEvent} event */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // Keep already-loaded slides for same-product carousel updates.
    // Price, availability and the picker still receive the normal response.
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker.huntech-product-page-picker');
    const localMap = this.querySelector('script[data-huntech-media-map]');
    const nextMap = newMediaGallery.querySelector('script[data-huntech-media-map]');
    if (picker && !picker.closest('product-card, quick-add-dialog, dialog') &&
        (this.presentation === 'carousel' || window.matchMedia('(max-width: 749px)').matches) &&
        localMap?.textContent && nextMap?.textContent && this.slideshow) {
      try {
        const current = JSON.parse(localMap.textContent);
        const next = JSON.parse(nextMap.textContent);
        const currentIds = Object.keys(current.indices ?? {}).sort();
        const nextIds = Object.keys(next.indices ?? {}).sort();
        const checkedColours = Array.from(picker.querySelectorAll('input:checked'));
        const colour = checkedColours.find((input) => current.options?.[input.dataset.optionValueId]);
        const mediaId = colour && current.options[colour.dataset.optionValueId];
        const index = current.indices?.[mediaId];
        if (current.productId === next.productId &&
            JSON.stringify(current.options) === JSON.stringify(next.options) &&
            JSON.stringify(currentIds) === JSON.stringify(nextIds) &&
            Number.isInteger(index) && index >= 0) {
          this.slideshow.select(index, undefined, { animate: false });
          return;
        }
      } catch (error) {
        console.warn('Gallery reuse unavailable:', error);
      }
    }
    this.replaceWith(newMediaGallery);
  };

  /** @param {ZoomMediaSelectedEvent} event */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
